import { API } from '../api/endpoints';

/**
 * Run Migrations from the avatar menu.
 *
 * GET /api/Migration/Run starts a forced re-run of this connection's
 * databases and returns 202 with its runId; this polls GET /api/Migration/Status
 * (the latest run of this connection's database, from the central run table,
 * so any server answers the same) until that run stops, then reads its results
 * from `history`. It used to wait on Run itself, but a run takes one to three
 * minutes on a database that is behind and Azure Front Door cuts a request off
 * at 30 seconds - the menu said "Network Error" while the run went on and
 * succeeded. A 409 means an update of this database is already running
 * (automatic or someone else's); its message says so.
 */

export interface MigrationEntry {
  script: string;
  database: string;
  status: string;
  error?: string | null;
  timestampUtc: string;
}

export interface MigrationOutcome {
  applied: number;
  failed: number;
  notFound: number;
  skipped: number;
  failures: MigrationEntry[];
}

/** What Run and Status answer (MigrationController); only the fields read here. */
interface RunResponse {
  startedUtc?: string;
  runId?: number;
  /** ?wait=true, or an API that still answers synchronously. */
  results?: MigrationEntry[];
}

interface StatusResponse {
  isRunning?: boolean;
  run?: { runId?: number } | null;
  history?: MigrationEntry[];
}

type Get = (url: string) => Promise<{ status: number; data: unknown }>;

/** Which scripts a run covers: the named ones only (Preview first), and whether to re-apply ones already applied. */
export interface RunFilter {
  only?: string[];
  force?: boolean;
}

export interface RunOptions {
  /** Without it the run is the tool's default: every script, re-applied. */
  filter?: RunFilter;
  pollMs?: number;
  timeoutMs?: number;
  /** Consecutive failed polls tolerated before giving up (a dropped poll is not a failed run). */
  maxPollErrors?: number;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export function summarize(entries: MigrationEntry[]): MigrationOutcome {
  const count = (status: string) => entries.filter((e) => e.status === status).length;
  return {
    applied: count('Applied'),
    failed: count('Failed'),
    notFound: count('NotFound'),
    skipped: count('Skipped'),
    failures: entries.filter((e) => e.status === 'Failed'),
  };
}

export async function runMigrations(get: Get, options: RunOptions = {}): Promise<MigrationOutcome> {
  const { filter, pollMs = 3000, timeoutMs = 15 * 60 * 1000, maxPollErrors = 5, sleep = defaultSleep, now = Date.now } = options;

  const start = await get(API.migrationRun(filter));
  const started = (start.data ?? {}) as RunResponse;

  // An API that still answers synchronously returns the results directly.
  if (start.status === 200 && Array.isArray(started.results)) {
    return summarize(started.results);
  }

  const startedUtc = Date.parse(started.startedUtc ?? '');
  const runId = started.runId;
  const deadline = now() + timeoutMs;
  let pollErrors = 0;

  while (now() < deadline) {
    await sleep(pollMs);
    let status: StatusResponse;
    try {
      status = ((await get(API.migrationStatus())).data ?? {}) as StatusResponse;
      pollErrors = 0;
    } catch (err) {
      if (++pollErrors >= maxPollErrors) throw err;
      continue;
    }
    if (status.isRunning) continue;

    const history = status.history ?? [];
    // Status describes the latest run of this database; it is ours when the ids match.
    if (runId && status.run?.runId) {
      if (status.run.runId !== runId) continue;
      return summarize(history);
    }
    const mine = Number.isNaN(startedUtc)
      ? history
      : history.filter((e) => Date.parse(e.timestampUtc) >= startedUtc);
    return summarize(mine);
  }

  throw new Error('The migration is still running after 15 minutes. Check again later.');
}

export function describeOutcome(o: MigrationOutcome): { text: string; ok: boolean } {
  const counts = `Applied ${o.applied} · Failed ${o.failed} · Not found ${o.notFound} · Skipped ${o.skipped}`;
  if (o.failed === 0) return { text: `Migration completed. ${counts}`, ok: true };
  const first = o.failures[0];
  const detail = first ? ` First failure: ${first.database} ${first.script}${first.error ? ` (${first.error})` : ''}` : '';
  return { text: `Migration completed with ${o.failed} failure(s). ${counts}.${detail}`, ok: false };
}

// ─── Preview (GET /api/Migration/Preview, MigrationController.Preview) ───

/** A row count for a data step. `value` null and `unknown` false = it doesn't apply here. */
export interface PreviewCount {
  key: string;
  label: string;
  value: number | null;
  unknown: boolean;
  note?: string | null;
}

export interface PreviewScript {
  lane: 'Server' | 'Facility' | 'Postgres' | string;
  script: string;
  path: string;
  wouldRun: boolean;
  /** New, Changed, RetryFailed, Forced, Repeatable, NoJournal. */
  reason?: string | null;
  /** NotRun, Applied, AppliedEarlierVersion, Changed, Failed, NewerBuild, NoJournal, Unknown. */
  state: string;
  types: string[];
  summary: string;
  webOnly: boolean;
  repeatable: boolean;
  skipped?: string | null;
  lastRunUtc?: string | null;
  lastBuild?: string | null;
  objects?: { total: number; missing: number; missingNames: string[] } | null;
  counts: PreviewCount[];
}

export interface MigrationPreview {
  connectionId: number;
  webVersionAllowed: boolean | null;
  connectionKind: string;
  build: string;
  force: boolean;
  /** What Run with the same parameters would answer instead of starting (a desktop connection), or null. */
  refused?: string | null;
  automatic: { enabled: boolean; reason: string };
  runInProgress?: number | null;
  databases: { lane: string; reachable: boolean; journalExists: boolean; upToDate: boolean; dbVersion?: number | null; edition?: string | null; error?: string | null }[];
  scripts: PreviewScript[];
  totals: { wouldRun: number; dataSteps: number; settingSteps: number; skipped: number; unknownCounts: number };
  elapsedMs: number;
}

export async function fetchMigrationPreview(get: Get, filter?: RunFilter): Promise<MigrationPreview> {
  const response = await get(API.migrationPreview(filter));
  return response.data as MigrationPreview;
}

/** "Duplicate special-needs rows deleted: 3" lines for the counts that say something (non-zero or unknown). */
export function describeCounts(script: PreviewScript): string[] {
  return script.counts
    .filter((c) => c.unknown || (c.value !== null && c.value !== 0))
    .map((c) => `${c.label}: ${c.unknown ? 'unknown' : c.value!.toLocaleString()}`);
}
